import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useT } from '../../i18n';

export interface SortableHeaderProps {
  /** The sort key this header controls. */
  sortKey: string;
  /** The currently active sort key. */
  currentSort: string;
  /** Current sort direction. */
  sortDir: 'asc' | 'desc';
  /** Invoked with this header's sortKey when clicked. */
  onClick: (sortKey: string) => void;
  /** Content alignment within the header button. */
  align?: 'left' | 'center' | 'right';
  /** Header label content. */
  children: ReactNode;
  /** Optional explicit title attribute. */
  title?: string;
}

/**
 * Clickable table header with sort indicator.
 * Extracted verbatim from the monolith (legacy/App.tsx); markup unchanged so
 * styling is identical.
 */
export function SortableHeader({
  sortKey,
  currentSort,
  sortDir,
  onClick,
  align = 'left',
  children,
  title,
}: SortableHeaderProps) {
  const t = useT();
  const isActive = currentSort === sortKey;
  const alignClass =
    align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';
  return (
    <button
      onClick={() => onClick(sortKey)}
      className={`flex items-center gap-1 ${alignClass} font-mono text-[11px] font-semibold uppercase tracking-wider transition-colors ${
        isActive ? 'text-ink' : 'text-ink-2 hover:text-ink'
      }`}
      title={
        title ||
        t('ui.sortableHeader.sortBy', {
          column: typeof children === 'string' ? children : sortKey,
        })
      }
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        textAlign: align,
      }}
    >
      <span>{children}</span>
      {isActive ? (
        sortDir === 'desc' ? (
          <ArrowDown size={11} strokeWidth={2.5} />
        ) : (
          <ArrowUp size={11} strokeWidth={2.5} />
        )
      ) : (
        <span style={{ width: 11, opacity: 0.3 }}>
          <ArrowDown size={11} strokeWidth={1.5} />
        </span>
      )}
    </button>
  );
}
